import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Stack,
  Chip,
  Typography,
  CircularProgress,
  Divider,
  TextField,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Comment as CommentIcon,
  Warning as WarningIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import {
  SiteIncident,
  SEVERITY_LABEL,
  STATUS_LABEL,
  IncidentSeverity,
  IncidentStatus,
  INCIDENT_STATUSES,
} from '../../../models/SiteIncident';
import { SiteIncidentService } from '../../../services/SiteIncidentService';
import { SiteIncidentComment } from '../../../models/SiteIncidentComment';
import { SitePhoto, isVideoPhoto } from '../../../models/SitePhoto';
import { SiteIncidentCommentService } from '../../../services/SiteIncidentCommentService';
import { SitePhotoService } from '../../../services/SitePhotoService';
import AuthorChip from '../../../components/AuthorChip';
import PhotoLightboxDialog from './PhotoLightboxDialog';

interface IncidentDetailDialogProps {
  open: boolean;
  incident: SiteIncident | null;
  comments: SiteIncidentComment[];
  photos: SitePhoto[];
  /** 현장 프로젝트 ID — 추가 사진 업로드에 필요. */
  projectId: number;
  onClose: () => void;
  onCommentCreated: (c: SiteIncidentComment) => void;
  /** 추가 업로드된 사진을 부모 state 에 반영. */
  onPhotoUploaded: (p: SitePhoto) => void;
  /** 상태 변경 성공 시 부모 목록 업데이트. 미제공이면 다이얼로그 내부 표시만 갱신. */
  onIncidentUpdated?: (i: SiteIncident) => void;
}

const SEVERITY_CHIP_COLOR: Record<
  IncidentSeverity,
  'default' | 'info' | 'warning' | 'error'
> = {
  low: 'info',
  medium: 'default',
  high: 'warning',
  critical: 'error',
};

const STATUS_CHIP_COLOR: Record<
  IncidentStatus,
  'default' | 'error' | 'info' | 'warning' | 'success'
> = {
  open: 'error',
  investigating: 'info',
  in_progress: 'warning',
  on_hold: 'default',
  resolved: 'success',
  closed: 'default',
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTimeShort = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 장애 이력 상세 보기 다이얼로그.
 *
 * - 게시물 메타·본문·비고
 * - 첨부 이미지 갤러리: 클릭 시 라이트박스 확대 (← / → / ESC 키보드 지원),
 *   각 썸네일에 다운로드·확대 액션 아이콘, 추가 업로드 버튼
 * - 댓글 목록 + 입력
 */
const IncidentDetailDialog: React.FC<IncidentDetailDialogProps> = ({
  open,
  incident,
  comments,
  photos,
  projectId,
  onClose,
  onCommentCreated,
  onPhotoUploaded,
  onIncidentUpdated,
}) => {
  const [commentBody, setCommentBody] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  // 추가 이미지 업로드
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // 라이트박스
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // 상태 변경
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const handleStatusChange = async (next: IncidentStatus) => {
    if (!incident || next === incident.status) return;
    setStatusSaving(true);
    setStatusError(null);
    try {
      const resolvedAt =
        next === 'resolved' || next === 'closed' ? new Date().toISOString() : null;
      const updated = await SiteIncidentService.update(incident.id, {
        status: next,
        resolved_at: resolvedAt,
      });
      onIncidentUpdated?.(updated);
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : '상태 변경 실패');
    } finally {
      setStatusSaving(false);
    }
  };

  // 다이얼로그가 닫힐 때 입력/에러 초기화
  React.useEffect(() => {
    if (!open) {
      setCommentBody('');
      setCommentAuthor('');
      setPostError(null);
      setUploadError(null);
      setLightboxOpen(false);
    }
  }, [open]);

  const handlePost = async () => {
    if (!incident) return;
    const body = commentBody.trim();
    if (!body) return;
    setPosting(true);
    setPostError(null);
    try {
      const created = await SiteIncidentCommentService.create(incident.id, {
        author: commentAuthor.trim() || undefined,
        body,
      });
      onCommentCreated(created);
      setCommentBody('');
    } catch (e) {
      setPostError(e instanceof Error ? e.message : '댓글 작성 실패');
    } finally {
      setPosting(false);
    }
  };

  const handleSelectFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!incident) return;
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    const failed: string[] = [];
    for (const f of files) {
      try {
        const uploaded = await SitePhotoService.upload(projectId, {
          file: f,
          category: 'incident',
          relatedIncidentId: incident.id,
          takenAt: incident.occurred_at,
        });
        onPhotoUploaded(uploaded);
      } catch (err) {
        failed.push(`${f.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    setUploading(false);
    if (failed.length > 0) {
      setUploadError(`업로드 실패 ${failed.length}건: ${failed.slice(0, 3).join(' / ')}`);
    }
  };

  const openLightboxAt = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  if (!incident) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            장애 이력이 선택되지 않았습니다.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const sev = SEVERITY_CHIP_COLOR[incident.severity] ?? 'default';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
          <WarningIcon fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {incident.title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="닫기">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {statusError && (
            <Alert
              severity="warning"
              onClose={() => setStatusError(null)}
              sx={{ mb: 1.5 }}
            >
              {statusError}
            </Alert>
          )}
          {/* 메타 chip 줄 */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ rowGap: 0.5, mb: 1.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
              {formatDateTime(incident.occurred_at)}
            </Typography>
            <Chip
              label={SEVERITY_LABEL[incident.severity]}
              size="small"
              color={sev}
              variant="outlined"
              sx={{ height: 22 }}
            />
            <FormControl size="small">
              <Select
                value={incident.status}
                onChange={(e: SelectChangeEvent<IncidentStatus>) =>
                  handleStatusChange(e.target.value as IncidentStatus)
                }
                disabled={statusSaving}
                renderValue={(value) => (
                  <Chip
                    label={STATUS_LABEL[value as IncidentStatus]}
                    size="small"
                    color={STATUS_CHIP_COLOR[value as IncidentStatus] ?? 'default'}
                    variant="outlined"
                    sx={{ height: 22, cursor: 'pointer' }}
                  />
                )}
                sx={{
                  '& .MuiSelect-select': { py: 0.25, px: 0.5 },
                  '& fieldset': { border: 'none' },
                }}
                inputProps={{ 'aria-label': '상태 변경' }}
              >
                {INCIDENT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s} dense>
                    <Chip
                      label={STATUS_LABEL[s]}
                      size="small"
                      color={STATUS_CHIP_COLOR[s] ?? 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </MenuItem>
                ))}
                {incident.status === 'closed' && (
                  <MenuItem value="closed" sx={{ display: 'none' }}>
                    완료 (legacy closed)
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            {statusSaving && <CircularProgress size={14} />}
            {incident.service_name && (
              <Chip
                label={incident.service_name}
                size="small"
                variant="filled"
                sx={{ height: 22, bgcolor: 'action.hover' }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            {incident.assignee && (
              <Tooltip title="담당자">
                <Typography variant="caption" color="text.secondary">
                  담당: {incident.assignee}
                </Typography>
              </Tooltip>
            )}
          </Stack>

          {/* 본문 */}
          {incident.summary && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 0.5 }}
              >
                내용
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {incident.summary}
              </Typography>
            </Box>
          )}

          {incident.remark && (
            <Box
              sx={{
                mt: 1,
                mb: 1.5,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ display: 'block', mb: 0.5 }}
              >
                비고
              </Typography>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                {incident.remark}
              </Typography>
            </Box>
          )}

          {/* 첨부 이미지 갤러리 */}
          <Box sx={{ mb: 1.5 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.75, justifyContent: 'space-between' }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                첨부 이미지 ({photos.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={14} /> : <AddPhotoIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? '업로드 중...' : '이미지 추가'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handleSelectFiles}
              />
            </Stack>

            {uploadError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {uploadError}
              </Alert>
            )}

            {photos.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  첨부 이미지가 없습니다. "이미지 추가" 버튼으로 사진을 올려보세요.
                </Typography>
              </Box>
            ) : (
              <ImageList cols={4} rowHeight={120} gap={6} sx={{ m: 0 }}>
                {photos.map((p, idx) => (
                  <ImageListItem
                    key={p.id}
                    sx={{
                      cursor: 'zoom-in',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      '&:hover .photo-actions': { opacity: 1 },
                    }}
                    onClick={() => openLightboxAt(idx)}
                  >
                    {isVideoPhoto(p) ? (
                      // 동영상 첨부는 <img> 로 그리면 엑박 — 첫 프레임 썸네일.
                      <video
                        src={SitePhotoService.fileUrl(p.id)}
                        muted
                        preload="metadata"
                        style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                      />
                    ) : (
                      <img
                        src={SitePhotoService.fileUrl(p.id)}
                        alt={p.original_name || p.filename}
                        loading="lazy"
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      />
                    )}
                    <ImageListItemBar
                      className="photo-actions"
                      sx={{
                        opacity: 0,
                        transition: 'opacity 120ms',
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%)',
                      }}
                      position="bottom"
                      actionPosition="right"
                      actionIcon={
                        <Stack direction="row" spacing={0.25} sx={{ pr: 0.5 }}>
                          <Tooltip title="확대 보기">
                            <IconButton
                              size="small"
                              sx={{ color: 'white' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                openLightboxAt(idx);
                              }}
                              aria-label="확대"
                            >
                              <ZoomInIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="다운로드">
                            <IconButton
                              size="small"
                              component="a"
                              href={SitePhotoService.fileUrl(p.id)}
                              download={p.original_name || p.filename}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'white' }}
                              onClick={(e) => e.stopPropagation()}
                              aria-label="다운로드"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 댓글 */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
            <CommentIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              댓글 {comments.length}
            </Typography>
          </Stack>

          {comments.length > 0 && (
            <Stack spacing={0.75} sx={{ mb: 1.5 }}>
              {comments.map((c) => (
                <Box key={c.id} sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    {/* 인증 사용자가 작성한 댓글이면 아바타+이름 칩. 없으면 legacy author 텍스트. */}
                    {(c.created_by_name || c.created_by_id) ? (
                      <AuthorChip name={c.created_by_name || c.author} userId={c.created_by_id} label="" dense />
                    ) : (
                      <Typography variant="caption" fontWeight={600}>
                        {c.author || '익명'}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {formatDateTimeShort(c.created_at)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ whiteSpace: 'pre-wrap', display: 'block' }}
                  >
                    {c.body}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              placeholder="작성자"
              value={commentAuthor}
              onChange={(e) => setCommentAuthor(e.target.value)}
              sx={{ width: 120, flexShrink: 0 }}
            />
            <TextField
              size="small"
              placeholder="댓글을 입력하세요 (Cmd/Ctrl+Enter 로 빠른 등록)"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              fullWidth
              multiline
              maxRows={4}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  if (!posting && commentBody.trim()) handlePost();
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handlePost}
              disabled={posting || !commentBody.trim()}
              aria-label="댓글 등록"
            >
              {posting ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Stack>
          {postError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {postError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>닫기</Button>
        </DialogActions>
      </Dialog>

      <PhotoLightboxDialog
        open={lightboxOpen}
        photos={photos}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

export default IncidentDetailDialog;
